import { useAuth0 } from '@auth0/auth0-react';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { authHooks } from '~/shared/api/generated';
import { getErrorMessage } from '~/shared/lib/get-error-message';
import { AuthenticatedUser, useAuthStore } from '~/shared/model/auth';

import { useLogout } from '~/features/authorization/logout';

export function useSaveUser() {
  const { isLoading, isAuthenticated, user: auth0User } = useAuth0();
  const { isAuthorized, user: authStoreUser, setUser } = useAuthStore();
  const { handleLogout } = useLogout();

  const { mutateAsync: saveUserToDatabase } =
    authHooks.useAuthControllerRegisterUserIfHeIsNotRegisteredYet({
      mutation: {
        onSuccess: (data) => {
          const maybeUser = data?.data;
          if (maybeUser) {
            setUser({
              ...(maybeUser as unknown as object),
              _source: 'backend',
            } as AuthenticatedUser);
          }
        },
        onError: async (error: unknown) => {
          if (
            error instanceof AxiosError &&
            typeof error.response === 'object' &&
            typeof error.response.data === 'object' &&
            'message' in error.response.data
          ) {
            toast.error(error.response?.data.message);
          } else {
            toast.error(getErrorMessage(error));
          }
          handleLogout();
        },
      },
    });

  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      isAuthorized &&
      auth0User &&
      authStoreUser &&
      // eslint-disable-next-line no-underscore-dangle
      authStoreUser._source === 'after_logging_in'
    ) {
      saveUserToDatabase({
        firstName: auth0User.given_name ?? 'no first name',
        lastName: auth0User.family_name ?? 'no last name',
        email: auth0User.email ?? `${Date.now()}@gmail.com`,
        avatar: auth0User.picture ?? null,
        role: authStoreUser.role,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }
  }, [
    isLoading,
    isAuthenticated,
    isAuthorized,
    auth0User,
    authStoreUser,
    saveUserToDatabase,
  ]);
}
